<script setup>
import { PhxButton, PhxStrapFinderPicture } from '@phx-styleguides/iwc';
import { computed } from 'vue';
import { emits as defaultEmits, props as defaultProps, useSection } from '@maison/composables/strapFinder/useSection';
import { useI18n } from 'vue-i18n';
import ConfiguratorSectionLayout from '@maison/components/strapFinder/sections/ConfiguratorSectionLayout.vue';
import StrapFinderProductCard from '@maison/components/strapFinder/StrapFinderProductCard.vue';
import { useAddToBag } from '@maison/composables/strapFinder/useAddToBag';

const props = defineProps({
  ...defaultProps,
  selectedStrap: {
    type: Object,
    default: null,
  },
  currentStrapSize: {
    type: String,
    default: null,
  },
  currentBuckle: {
    type: String,
    default: null,
  },
});

defineEmits(defaultEmits);

const { id, preTitle } = useSection('summary');
const { isLoading, addReferencesToBag } = useAddToBag();

const { t } = useI18n();

const products = computed(() => {
  const items = [];
  if (!props.selectedStrap || !props.currentStrapSize) return items;

  // We need to make sure we display the price and reference of the correct strap size
  const strapSize = props.selectedStrap.sizes[props.currentStrapSize];

  items.push({
    reference: strapSize.reference,
    name: props.selectedStrap.name,
    media: {
      id: props.selectedStrap.modelReference,
      captionText: props.selectedStrap.name,
      ...props.selectedStrap.image,
    },
    image: props.selectedStrap.image,
    price: strapSize.priceWithCurrency,
    size: t(`StrapFinderConfigurator.sizes.${props.currentStrapSize}`),
  });

  if (props.currentBuckle && props.currentBuckle !== 'no-buckle') {
    const buckle = props.selectedStrap.buckles.find((b) => b.modelReference === props.currentBuckle);
    if (buckle) {
      items.push({
        reference: buckle.modelReference,
        name: buckle.name,
        image: buckle.image,
        price: buckle.priceWithCurrency,
      });
    }
  }

  return items;
});
</script>

<template>
  <configurator-section-layout :id="id" :title="title" :description="description" :memo="memo" :pre-title="preTitle">
    <template #media>
      <phx-strap-finder-picture
        v-if="selectedStrap"
        :id="`${id}-media`"
        :caption-text="selectedStrap.name"
        v-bind="selectedStrap.image"
      />
    </template>

    <template #default>
      <div class="stack stack--spacing-4">
        <strap-finder-product-card v-for="product in products" :key="product.reference" v-bind="product" />
      </div>
    </template>

    <template #action>
      <phx-button
        :loading="isLoading"
        class="btn--plain btn--primary"
        :label="$t('StrapFinderConfigurator.summary.addToBag')"
        @click="addReferencesToBag(products.map((p) => p.reference))"
      />
    </template>
  </configurator-section-layout>
</template>
